import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheckIcon } from 'lucide-react';
import { AdminPageTitle, AdminSearch, ConfirmationModal, FilterBar, FilterSelect, Panel, StatusBadge, UserCell } from '../../components/admin/AdminPrimitives';
import { DataTable, type Column } from '../../components/admin/DataTable';
import { adminUsers, permissionMatrix, recordAudit, type AdminUser, type UserStatus } from '../../data/admin';
import { prototypeAdminRole } from '../../data/adminOperations';
const statusLabels: Record<UserStatus, string> = {
  active: 'Ativo',
  inactive: 'Inativo',
  suspended: 'Suspenso',
  new: 'Novo'
};
export function AdminUsers() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [role, setRole] = useState('all');
  const [pending, setPending] = useState<{
    user: AdminUser;
    action: 'suspend' | 'reactivate';
  } | null>(null);
  const [suspended, setSuspended] = useState<string[]>([]);
  const rows = useMemo(() => {
    const term = query.trim().toLowerCase();
    return adminUsers.map(user => suspended.includes(user.id) ? {
      ...user,
      status: 'suspended' as UserStatus
    } : user).filter(user => {
      if (status !== 'all' && user.status !== status) return false;
      if (role !== 'all' && user.role !== role) return false;
      if (!term) return true;
      return user.name.toLowerCase().includes(term) || user.username.toLowerCase().includes(term) || user.email.toLowerCase().includes(term);
    });
  }, [query, status, role, suspended]);
  const canViewGamification = (permissionMatrix[prototypeAdminRole].analytics ?? []).includes('view');
  const columns: Column<AdminUser>[] = [{
    id: 'user',
    header: 'Usuário',
    width: '26%',
    sortValue: row => row.name,
    cell: row => <UserCell user={row} secondary={row.username} data-id="element-2377" data-mp="3kztp4s1epgo7" />
  }, {
    id: 'email',
    header: 'E-mail',
    width: '20%',
    sortValue: row => row.email,
    cell: row => <span className="text-muted" data-id="element-2378" data-mp="1tvi0oavyj903">{row.email}</span>
  }, {
    id: 'status',
    header: 'Status',
    sortValue: row => row.status,
    cell: row => <StatusBadge status={row.status} label={statusLabels[row.status]} data-id="element-2379" data-mp="22v51ovkrycsm" />
  }, {
    // Administrative access only — consumer accounts have no in-app roles.
    // The admin role itself is managed exclusively in Admin → Equipe.
    id: 'role',
    header: 'Acesso administrativo',
    sortValue: row => row.role,
    cell: row => row.role === 'admin' ? <span className="text-[12.5px] font-medium text-deep" data-id="element-2380" data-mp="2micjl3d4m0bg">Membro da equipe</span> : <span className="text-[12.5px] text-muted" data-id="element-2381" data-mp="2qkfzc46tvr2j">Sem acesso Admin</span>
  }, {
    id: 'registered',
    header: 'Cadastro',
    sortValue: row => row.registered,
    cell: row => <span className="bee-num text-muted" data-id="element-2382" data-mp="jwgxa43mdwb3">{row.registered}</span>,
    optional: true
  }, {
    id: 'lastActive',
    header: 'Última atividade',
    sortValue: row => row.lastActive,
    cell: row => <span className="text-muted" data-id="element-2383" data-mp="av9cmou10tzh">{row.lastActive}</span>
  },
  // Gamification data: only for roles with Analytics · View; can be hidden via Colunas.
  ...(canViewGamification ? [{
    id: 'streak',
    header: 'Sequência',
    align: 'right' as const,
    optional: true,
    sortValue: (row: AdminUser) => row.streak,
    cell: (row: AdminUser) => <span className="bee-num font-bold text-deep" data-id="element-2384" data-mp="tr8x7ekocv91">
                {row.streak > 0 ? `${row.streak} d` : '—'}
              </span>
  }] : [])];
  return <div className="space-y-4" data-id="element-2385" data-mp="1mbplu0ezmedw">
      <AdminPageTitle title="Usuários" subtitle="Gestão operacional de contas. Métricas de saúde individuais não aparecem nesta lista." data-id="element-2386" data-mp="1himr3oyh4yw4" />

      <FilterBar data-id="element-2387" data-mp="11600op5h02af">
        <AdminSearch value={query} onChange={setQuery} placeholder="Buscar por nome, usuário ou e-mail" data-id="element-2388" data-mp="162i1xf1ldii0" />
        <FilterSelect label="Status" value={status} onChange={setStatus} options={[{
        id: 'all',
        label: 'Todos'
      }, {
        id: 'active',
        label: 'Ativos'
      }, {
        id: 'inactive',
        label: 'Inativos'
      }, {
        id: 'suspended',
        label: 'Suspensos'
      }, {
        id: 'new',
        label: 'Novos'
      }]} data-id="element-2389" data-mp="s4a83fzuqxb2" />
        <FilterSelect label="Acesso administrativo" value={role} onChange={setRole} options={[{
        id: 'all',
        label: 'Todos'
      }, {
        id: 'user',
        label: 'Sem acesso Admin'
      }, {
        id: 'admin',
        label: 'Membro da equipe'
      }]} data-id="element-2390" data-mp="1b0rxvrv54n4o" />
      </FilterBar>

      <DataTable rows={rows} columns={columns} rowKey={row => row.id} onRowClick={row => navigate(`/admin/usuarios/${row.id}`)} emptyTitle="Nenhum usuário encontrado" emptyBody="Revise a busca ou os filtros de status e papel." actions={[{
      id: 'view',
      label: 'Ver detalhe',
      onSelect: row => navigate(`/admin/usuarios/${row.id}`)
    }, {
      id: 'moderation',
      label: 'Histórico de moderação',
      onSelect: row => navigate(`/admin/usuarios/${row.id}`)
    }, {
      id: 'suspend',
      label: 'Suspender conta',
      destructive: true,
      onSelect: row => setPending({
        user: row,
        action: 'suspend'
      })
    }, {
      id: 'reactivate',
      label: 'Reativar conta',
      onSelect: row => setPending({
        user: row,
        action: 'reactivate'
      })
    }]} data-id="element-2391" data-mp="28ijmpieal7op" />

      <Panel title="Privacidade nesta tela" data-id="element-2392" data-mp="3fdisnltnu9ol">
        <p className="flex items-start gap-2 text-[12.5px] leading-relaxed text-muted" data-id="element-2393" data-mp="3ncvstr0bebun">
          <ShieldCheckIcon size={15} className="mt-[1px] shrink-0 text-gold" aria-hidden="true" data-id="element-2394" data-mp="3rd6a3ztk4oot" />
          Peso, composição corporal e demais dados de saúde não são listados aqui. O acesso a
          qualquer informação sensível segue o princípio de menor privilégio e é registrado na
          auditoria. "Acesso administrativo" indica apenas se a conta integra a equipe do
          Admin; papéis e permissões são geridos somente em Equipe.
        </p>
      </Panel>

      <ConfirmationModal open={pending !== null} destructive={pending?.action === 'suspend'} title={pending?.action === 'suspend' ? 'Suspender esta conta?' : 'Reativar esta conta?'} body={pending?.action === 'suspend' ? `${pending?.user.name} perde o acesso imediatamente e o conteúdo público sai do ar. A ação é registrada na auditoria com o seu nome.` : `${pending?.user.name} volta a ter acesso completo. A ação é registrada na auditoria com o seu nome.`} confirmLabel={pending?.action === 'suspend' ? 'Suspender conta' : 'Reativar conta'} requireText={pending?.action === 'suspend' ? 'SUSPENDER' : undefined} onConfirm={() => {
      if (pending?.action === 'suspend') {
        setSuspended(prev => [...prev, pending.user.id]);
      } else if (pending) {
        setSuspended(prev => prev.filter(id => id !== pending.user.id));
      }
      if (pending) {
        const suspend = pending.action === 'suspend';
        recordAudit({
          action: suspend ? {
            'pt-BR': 'Suspendeu conta',
            'en-US': 'Suspended account'
          } : {
            'pt-BR': 'Reativou conta',
            'en-US': 'Reactivated account'
          },
          entity: pending.user.id,
          context: {
            'pt-BR': 'Gestão de usuários',
            'en-US': 'User management'
          },
          before: suspend ? 'active' : 'suspended',
          after: suspend ? 'suspended' : 'active',
          severity: 'sensitive'
        });
      }
      setPending(null);
    }} onClose={() => setPending(null)} data-id="element-2395" data-mp="38kvbwj9ulex1" />
    </div>;
}